import React from 'react';
import { Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  REVOKE_ADDRESS_ABNORMAL,
  ReportAddressAbnormalInput,
  RevokeAddressAbnormalResponse,
} from '../graphql/mutations/addressAbnormal';
import { ReviewAbnormalReportData } from '../types/reviewPool';
import { OnsiteProofs } from '../utils/followUpTimeline';
import { formatHubTimeFull, useHubZone } from '../utils/hubTime';

interface Props {
  report: ReviewAbnormalReportData;
  /** 审核池路径：条目 id。与 cooperationWarehouseId 二选一 */
  reviewPoolId?: number;
  /** 司机反馈跟进路径：站点 id */
  cooperationWarehouseId?: number;
  /**
   * 撤销入口是否可用。
   *
   * <p>站点已进入「确认无法建联」后必须传 false —— 状态机没有回退，撤销上报改不回站点状态，
   * 后端会拒绝。让按钮亮着等运营点了才报错，比一开始就置灰更糟：他会以为地址已恢复揽收。
   */
  revocable?: boolean;
  /**
   * 编辑入口是否可用。
   *
   * <p>灰度开关关闭时必须传 false：编辑走的是 reportAddressAbnormal，后端会校验灰度并返回
   * FEATURE_DISABLED。而**卡片本身照常展示、撤销照常可用** —— 已上报的记录改变了流程
   * （免上门直进二次建联），关开关止血后运营仍须看得见它、并且能把它撤回去。
   */
  editable?: boolean;
  onOpenEdit: () => void;
  /** 撤销成功后回调（调用方重新拉取条目或站点） */
  onRevoked: () => void;
}

/**
 * 「已上报地址异常」记录卡（2.2 需求点1）。
 *
 * <p>展示上报时间 + 原因 + 凭证 + 「编辑 / 撤销上报」。
 * 挂在联系结果确认之后、二次建联之前 —— 它正是让二次建联免掉司机上门的那个前提。
 *
 * <p>凭证复用 2.1 已上线的 {@link OnsiteProofs}（antd Image.PreviewGroup）而不是自己写 img 标签：
 * PRD 要求「支持图片的放大查看」，那个组件自带点击放大与多图左右切换，且在预签名 URL 取不到时
 * 会退化成占位块而非空白。司机上门凭证、联络记录截图都在用它，行为一致。
 *
 * <p><b>时间格式化在本组件内部完成</b>，调用方一律传原始 ISO 串。此前三个调用方各自决定要不要
 * 先 fmt 一次，结果审核池那边把 ISO 原文直接显示给了运营。口径收在一处才不会再漂。
 */
const AbnormalReportCard: React.FC<Props> = ({
  report,
  reviewPoolId,
  cooperationWarehouseId,
  revocable = true,
  editable = true,
  onOpenEdit,
  onRevoked,
}) => {
  const { t } = useTranslation();
  // 时间按 hub 时区展示：服务端是 UTC 无后缀串，裸 new Date 会当成浏览器本地
  const hubZone = useHubZone();
  const wt = (k: string) => t(`pickup.addressReviewPool.abnormal.${k}`);

  const [revokeMutation, { loading }] = useMutation<
    RevokeAddressAbnormalResponse,
    { input: ReportAddressAbnormalInput }
  >(REVOKE_ADDRESS_ABNORMAL, { fetchPolicy: 'no-cache' });

  // 原因是自由输入且非必填，直接显示原文；为空时用占位符而不是空白（空白看不出是"没填"还是"没加载出来"）
  const reasonLabel = report.reason?.trim() || wt('reasonEmpty');

  const handleRevoke = () => {
    Modal.confirm({
      title: wt('revokeConfirmTitle'),
      content: wt('revokeConfirmContent'),
      okText: wt('revokeCta'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const resp = await revokeMutation({
            variables: { input: { reviewPoolId, cooperationWarehouseId } },
          });
          if (!resp.data?.revokeAddressAbnormal) {
            message.error(wt('revokeFailed'));
            return;
          }
          message.success(wt('revokeSucceeded'));
          onRevoked();
        } catch (e: any) {
          const keys: string[] = (e?.graphQLErrors ?? [])
            .map((ge: any) => ge?.extensions?.messageKey)
            .filter(Boolean);
          message.error(keys.length ? t(keys[0]) : e?.message || wt('revokeFailed'));
        }
      },
    });
  };

  return (
    <div
      style={{
        background: '#fdf7f1',
        border: '1px solid #f3ddc0',
        borderRadius: 12,
        padding: '16px 18px',
        marginBottom: 22,
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#b5713e' }}>⚠️ {wt('reportedTitle')}</span>
          <span style={{ fontSize: 11, color: '#9a95ab' }}>{formatHubTimeFull(report.reportedAt, hubZone)}</span>
          {report.reportedByName && (
            <span style={{ fontSize: 11, color: '#9a95ab' }}>· {report.reportedByName}</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#6b6778', lineHeight: 1.6, marginBottom: 8 }}>
          <span style={{ color: '#928da0' }}>{wt('reasonInline')}</span>
          {reasonLabel}
        </div>
        {/* 备注单独展示：它和原因都非必填，可能备注才是唯一有信息的那个 */}
        {report.remark?.trim() && (
          <div style={{ fontSize: 12, color: '#6b6778', lineHeight: 1.6, marginBottom: 8 }}>
            <span style={{ color: '#928da0' }}>{wt('remarkInline')}</span>
            {report.remark}
          </div>
        )}
        {/* 全部凭证都渲染（此前只显示第 1 张，多张的其余照片运营根本看不到），点击可放大 */}
        <OnsiteProofs keys={report.proofKeys} urls={report.proofUrls} label={wt('proofLabel')} />
      </div>
      <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
        {editable ? (
          <a style={{ fontSize: 12, fontWeight: 700, color: '#b5713e' }} onClick={onOpenEdit}>
            {wt('editAction')}
          </a>
        ) : (
          // 灰度未开放：保留文案位置并给出去处，避免运营以为"编辑按钮消失了"
          <span style={{ fontSize: 12, color: '#c4bfd0' }} title={wt('editDisabledHint')}>
            {wt('editAction')}
          </span>
        )}
        {revocable ? (
          <a
            style={{ fontSize: 12, fontWeight: 700, color: '#928da0' }}
            onClick={loading ? undefined : handleRevoke}
          >
            {wt('revokeAction')}
          </a>
        ) : (
          // 不可撤销时保留位置但给出去处，避免运营反复找"撤销在哪"
          <span style={{ fontSize: 12, color: '#c4bfd0' }} title={wt('revokeDisabledHint')}>
            {wt('revokeAction')}
          </span>
        )}
      </div>
    </div>
  );
};

export default AbnormalReportCard;
